import { trackDeclineIdentity } from "@/amplitude";
import { usePostCandidateLogoutMutation } from "@/queries/auth/mutations";
import { TestType } from "@/queries/types";
import { formatTimeLimit } from "@/utils";
import styled from "@emotion/styled";
import { faArrowRight } from "@fortawesome/pro-regular-svg-icons";
import { DSButton } from "@hundred5/design-system";
import { useLingui } from "@lingui/react";
import { Plural, Trans } from "@lingui/react/macro";
import { useQueryClient } from "@tanstack/react-query";
import Router, { useRouter } from "next/router";
import { Icon } from "../icon";
import { QuestionCountByCategories } from "./types/test";

interface ConfirmStartProps {
  testType: TestType;
  duration: number;
  questionCount: number;
  testSlug: string;
  jobOpeningSlug: string;
  questionCountByCategories: QuestionCountByCategories;
  previousApplicationAt?: string | null;
  startTest: () => void;
  startTestPending?: boolean;
}

export function ConfirmStart({
  testType,
  duration,
  questionCount,
  jobOpeningSlug,
  questionCountByCategories,
  previousApplicationAt,
  startTest,
  startTestPending,
}: ConfirmStartProps) {
  const router = useRouter();
  const { i18n } = useLingui();
  const candidateLogoutMutation = usePostCandidateLogoutMutation();
  const queryClient = useQueryClient();
  const multipleChoiceQuestions =
    questionCountByCategories.multipleChoice > 0 ? (
      <>
        {questionCountByCategories.multipleChoice}{" "}
        <Trans comment="(The test includes x) multiple choice (and y open-ended questions)">
          multiple choice
        </Trans>
      </>
    ) : null;

  const openEndedQuestions =
    questionCountByCategories.openEnded > 0 ? (
      <>
        {questionCountByCategories.openEnded}{" "}
        <Trans comment="(The test includes x multiple choice and y) open-ended (questions)">
          open-ended
        </Trans>
      </>
    ) : null;

  const and =
    questionCountByCategories.multipleChoice > 0 &&
    questionCountByCategories.openEnded > 0 ? (
      <>
        {" "}
        <Trans>and</Trans>{" "}
      </>
    ) : null;

  return (
    <>
      {previousApplicationAt ? (
        <RetakeBanner>
          <Trans comment="You last took this test on May 21, 2022">
            You last took this test on
          </Trans>
          <span>
            {" "}
            {i18n.date(new Date(previousApplicationAt), {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </span>
        </RetakeBanner>
      ) : null}
      <Container>
        <Content>
          <Header>
            <Trans comment="You have (3 minutes left)">You have</Trans>
            <TimeLimit> {formatTimeLimit(duration)} </TimeLimit>
            <Plural
              value={questionCount}
              one="to answer one question"
              other="to answer # questions"
              comment="(You have 3 minutes) to answer one question"
            />
            {testType === "quiz" && (
              <QuestionTypesInfo>
                <Plural
                  value={
                    questionCountByCategories.openEnded ||
                    questionCountByCategories.multipleChoice
                  }
                  one={`The test includes ${multipleChoiceQuestions}${and}${openEndedQuestions} question.`}
                  other={`The test includes ${multipleChoiceQuestions}${and}${openEndedQuestions} questions.`}
                  comment="The test includes (x multiple choice | x multiple choice and y open-ended | y open-ended) question|s."
                />
              </QuestionTypesInfo>
            )}
          </Header>

          <Description>
            <Note>
              <Trans>Before you start:</Trans>
            </Note>
            <List>
              <ListItem>
                <Trans>
                  Make sure your internet is reliable: you must complete this
                  test in one session.
                </Trans>
              </ListItem>
              <ListItem>
                <Trans>You can move between questions.</Trans>
              </ListItem>
              <ListItem>
                <Trans>
                  You can go back and change your answers before submitting the
                  test.
                </Trans>
              </ListItem>
            </List>
          </Description>

          <Buttons>
            <StyledButton
              variant="secondary"
              size="big"
              onClick={() => {
                candidateLogoutMutation.mutate(
                  {
                    job_opening_slug: jobOpeningSlug,
                  },
                  {
                    onSuccess: () => {
                      queryClient.resetQueries();
                      console.log("queryClient clear");
                      if (router.asPath.includes("practice")) {
                        Router.replace(router.asPath.replace("/practice", ""));
                      }
                      trackDeclineIdentity(jobOpeningSlug);
                    },
                  },
                );
              }}
            >
              <Trans>Not now</Trans>
            </StyledButton>
            <StyledButton
              variant="primary"
              size="big"
              onClick={startTest}
              disabled={startTestPending}
            >
              <span>
                {startTestPending ? (
                  <Trans comment="Start button when pending">Starting...</Trans>
                ) : (
                  <Trans comment="Start button">Start</Trans>
                )}
              </span>
              <Icon icon={faArrowRight} color="white" />
            </StyledButton>
          </Buttons>
        </Content>
      </Container>
    </>
  );
}

//region Styles
const Container = styled.div`
  background: ${(props) => props.theme.colors.white};
  padding: 16px 16px 16px;
  position: relative;
  display: flex;
  justify-content: left;
  border-radius: 8px;
  height: 100%;

  @media (min-width: 768px) {
    height: auto;
    margin-top: 0;
    padding: 36px 36px 24px;
  }
`;

const Content = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: ${(props) => props.theme.colors.white};
`;

const RetakeBanner = styled.div`
  background-color: ${(props) => props.theme.colors.yellow[20]};
  border-radius: 8px;
  padding: 4px 8px;
  margin: 64px 16px 8px 16px;

  @media (min-width: 768px) {
    width: 100%;
    margin: 0 0 8px 0;
  }
`;

const Header = styled.h2`
  font-size: 20px;
  font-weight: 700;
  padding-bottom: 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.purple[10]};
`;

const QuestionTypesInfo = styled.div`
  font-size: 14px;
  font-weight: 400;
  color: ${(props) => props.theme.colors.purple[100]};
  margin-top: 8px;
`;

const TimeLimit = styled.strong`
  color: ${(props) => props.theme.colors.orange[100]};
  font-weight: 700;
`;

const Description = styled.div``;

const List = styled.ul`
  color: ${(props) => props.theme.colors.purple[100]};
  padding-left: 16px;
  margin-top: 12px;
`;

const ListItem = styled.li`
  font-size: 12px;
  margin-bottom: 8px;
`;

const Note = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.theme.colors.purple[100]};
  margin-bottom: 12px;
`;

const Buttons = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column-reverse;
  gap: 8px;
  margin-top: 36px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: right;
  }
`;

const StyledButton = styled(DSButton)`
  width: 100%;

  @media (min-width: 768px) {
    width: auto;
  }
`;
//endregion
